import React, {
  useEffect,
  useCallback,
  useMemo,
  useState,
  useContext,
} from 'react';

import { RouteComponentProps } from 'react-router-dom';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import catchOnly from 'utils/catchOnly';

import FmcForm from 'components/FmcForm';
import OtpVerificationView from './OtpVerificationView';
import ConsumerSearchContext from '../ConsumerSearchContext';

import useOTPSender, { OTPSenderError } from 'hooks/requests/otpSender';
import useTimeout from 'hooks/useTimeout';

import useOTPVerification, {
  OTPVerificationError,
} from 'hooks/requests/otpVerification';

import useTranslate from 'hooks/useTranslate';
import { selectAttempts, selectExpireTime } from 'state/otpCode';
import { NonClientError } from 'hooks/requests/fetchUtils';
import { isOtpCode } from 'utils/validations';

type PropTypes = RouteComponentProps & {
  onOtpSuccessful: (otpCode: string) => Promise<void>;
  values: {
    mobilePhone: string;
  };
};

export type FormValues = {
  otpCode: string;
};

const initialValues: FormValues = {
  otpCode: '',
};

const OtpVerification: React.FC<PropTypes> = props => {
  const t = useTranslate();
  const [verifyOTPCode] = useOTPVerification();
  const [sendOTP] = useOTPSender();
  const otpAttempts = useSelector(selectAttempts);
  const otpExpireTime = useSelector(selectExpireTime);
  const { enqueueSnackbar } = useSnackbar();
  const { onOtpSuccessful, values } = props;
  const { i18nPrefix } = useContext(ConsumerSearchContext);

  const schema = Yup.object().shape({
    otpCode: Yup.string()
      .label(t('formLabel.otpCode'))
      .concat(isOtpCode)
      .required(),
  });

  const tooManyAttempts = useMemo(() => otpAttempts >= 3, [otpAttempts]);
  const attemptsRemaining = useMemo(() => {
    if (otpAttempts === 0) {
      return null;
    } else if (otpAttempts >= 3) {
      return t(`${i18nPrefix}otpVerification.outOfAttempts`);
    } else {
      return t(`${i18nPrefix}otpVerification.attemptsLeft`, {
        count: 3 - otpAttempts,
      });
    }
  }, [i18nPrefix, otpAttempts, t]);

  const [setTimeout] = useTimeout();

  const [timedOut, setTimedOut] = useState<boolean>(false);
  //this needs to be changed when the workflow is changed

  const onSubmit = useCallback(
    async (values: FormValues) => {
      try {
        await verifyOTPCode({ otpCode: values.otpCode });
        await onOtpSuccessful(values.otpCode);
      } catch (e) {
        if (catchOnly(e, OTPVerificationError, NonClientError)) {
          if (e instanceof NonClientError) {
            enqueueSnackbar(t(`${i18nPrefix}otpVerification.serverError`), {
              variant: 'error',
              persist: true,
            });
          }
        }
      }
    },
    [i18nPrefix, t, verifyOTPCode, onOtpSuccessful, enqueueSnackbar],
  );

  useEffect(() => {
    if (otpExpireTime && otpExpireTime > Date.now()) {
      setTimedOut(false);
      setTimeout(() => setTimedOut(true), otpExpireTime - Date.now());
    } else {
      setTimedOut(true);
    }
  }, [otpExpireTime, setTimeout, setTimedOut]);

  const resendSms = useCallback(async () => {
    try {
      if (values.mobilePhone === null || values.mobilePhone === '') {
        throw new Error('Mobile phone must be present');
      }
      await sendOTP({ phone: values.mobilePhone });
      enqueueSnackbar(t(`${i18nPrefix}otpVerification.resendSmsSuccess`), {
        variant: 'success',
        persist: true,
      });
    } catch (e) {
      if (catchOnly(e, NonClientError, OTPSenderError)) {
        enqueueSnackbar(t(`${i18nPrefix}otpVerification.resendSmsError`), {
          variant: 'error',
          persist: true,
        });
      }
    }
  }, [values, sendOTP, enqueueSnackbar, t, i18nPrefix]);

  return (
    <FmcForm
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={schema}
    >
      {formProps => (
        <OtpVerificationView
          {...formProps}
          resendSms={resendSms}
          attemptsRemaining={attemptsRemaining}
          tooManyAttempts={tooManyAttempts}
          timedOut={timedOut}
        />
      )}
    </FmcForm>
  );
};

export default OtpVerification;
